import { hasPermission } from '@safepos/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  api,
  isImpersonating,
  onSessionExpired,
  platformApi,
  refreshSession,
  setImpersonation,
  setToken,
} from './api';

export interface Me {
  user: { id: string; name: string; email: string; totpEnabled: boolean };
  role: { id: string; name: string };
  permissions: string[];
  tenant: { id: string; name: string; businessType: string; status: string };
  outletIds: string[];
  /** Fitur efektif tenant (paket + penimpaan Super Admin). */
  features: Record<string, boolean>;
  /** Terisi bila ini sesi dukungan Super Admin. */
  impersonation?: { by: string };
}

const IMP_KEY = 'sp.imp'; // sessionStorage: sesi dukungan milik tab ini
const HANDOFF_KEY = 'sp.imp.handoff'; // localStorage: serah-terima token dari tab Super Admin (dihapus segera)
export interface ImpSession {
  token: string;
  exp: number;
  tenantName: string;
}
const readImp = (): ImpSession | null => {
  try {
    const v = sessionStorage.getItem(IMP_KEY);
    const s = v ? (JSON.parse(v) as ImpSession) : null;
    return s && s.exp > Date.now() ? s : null;
  } catch {
    return null;
  }
};
/** Dipakai tab Super Admin: titipkan token sesi dukungan untuk diambil tab baru. */
export function handOffImpersonation(s: ImpSession) {
  localStorage.setItem(HANDOFF_KEY, JSON.stringify(s));
}
export const impersonationSession = readImp;

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: Me | null;
  can: (perm: string) => boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  register: (input: Record<string, unknown>) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = useCallback(async () => {
    const data = await api.get<Me>('/auth/me');
    setMe(data);
    setStatus('authenticated');
  }, []);

  useEffect(() => {
    (async () => {
      // Sesi dukungan: ambil token yang dititipkan tab Super Admin (sekali pakai), simpan hanya di tab ini
      try {
        if (new URLSearchParams(location.search).has('imp')) {
          const h = localStorage.getItem(HANDOFF_KEY);
          localStorage.removeItem(HANDOFF_KEY);
          if (h) sessionStorage.setItem(IMP_KEY, h);
          history.replaceState(null, '', location.pathname);
        }
      } catch {
        /* penyimpanan tidak tersedia */
      }
      const imp = readImp();
      if (imp) {
        setImpersonation(imp.token);
        await loadMe().catch(() => {
          setImpersonation(null);
          setStatus('anonymous');
        });
        return;
      }
      // Pulihkan sesi dari cookie refresh (jika ada)
      if (await refreshSession('tenant')) await loadMe().catch(() => setStatus('anonymous'));
      else setStatus('anonymous');
    })();
    return onSessionExpired((realm) => {
      if (realm === 'tenant') {
        setMe(null);
        setStatus('anonymous');
      }
    });
  }, [loadMe]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      me,
      can: (perm) => !!me && hasPermission(me.permissions, perm),
      login: async (email, password, totp) => {
        const r = await api.post<{ accessToken: string }>('/auth/login', {
          email,
          password,
          ...(totp ? { totp } : {}),
        });
        setToken('tenant', r.accessToken);
        await loadMe();
      },
      register: async (input) => {
        const r = await api.post<{ accessToken: string }>('/auth/register', input);
        setToken('tenant', r.accessToken);
        await loadMe();
      },
      logout: async () => {
        if (isImpersonating()) {
          // sesi dukungan: jangan menyentuh cookie/refresh token pemilik yang asli
          try {
            sessionStorage.removeItem(IMP_KEY);
          } catch {
            /* abaikan */
          }
          setImpersonation(null);
        } else await api.post('/auth/logout').catch(() => undefined);
        setToken('tenant', null);
        setMe(null);
        setStatus('anonymous');
      },
    }),
    [status, me, loadMe],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth di luar AuthProvider');
  return v;
}

// ---------- Super Admin ----------
interface PlatformState {
  status: 'loading' | 'anonymous' | 'authenticated';
  admin: { name: string; email: string } | null;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
}
const PCtx = createContext<PlatformState | null>(null);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<PlatformState['status']>('loading');
  const [admin, setAdmin] = useState<PlatformState['admin']>(null);
  useEffect(() => {
    refreshSession('platform').then((ok) => setStatus(ok ? 'authenticated' : 'anonymous'));
    return onSessionExpired((realm) => realm === 'platform' && setStatus('anonymous'));
  }, []);
  const value = useMemo<PlatformState>(
    () => ({
      status,
      admin,
      login: async (email, password, totp) => {
        const r = await platformApi.post<{
          accessToken: string;
          admin: { name: string; email: string };
        }>('/admin/auth/login', { email, password, ...(totp ? { totp } : {}) });
        setToken('platform', r.accessToken);
        setAdmin(r.admin);
        setStatus('authenticated');
      },
      logout: async () => {
        await platformApi.post('/admin/auth/logout').catch(() => undefined);
        setToken('platform', null);
        setStatus('anonymous');
      },
    }),
    [status, admin],
  );
  return <PCtx.Provider value={value}>{children}</PCtx.Provider>;
}
export function usePlatformAuth(): PlatformState {
  const v = useContext(PCtx);
  if (!v) throw new Error('usePlatformAuth di luar provider');
  return v;
}
